<template>
  <main class="flex min-h-screen bg-[#f6f8fb]">
    <section class="relative flex w-full flex-col px-8 py-8 lg:w-1/2 lg:px-14 lg:py-10 xl:w-[45%]">
      <div class="absolute left-8 top-8 flex rounded-full border border-[#dae4f0] bg-[#fcfeff] p-1 lg:left-14 lg:top-10">
        <button
          v-for="language in languages"
          :key="language.value"
          type="button"
          class="rounded-full px-3 py-1.5 text-xs font-semibold transition"
          :class="lang === language.value ? 'bg-[#030445] text-white' : 'text-[#8799b0] hover:text-[#030445]'"
          @click="switchLang(language.value)"
        >{{ language.label }}</button>
      </div>

      <div class="mx-auto mt-14 flex w-full max-w-[380px] flex-1 flex-col justify-center lg:mt-0">
        <div class="mb-10 flex items-center gap-4">
          <span class="flex h-16 w-16 items-center justify-center rounded-2xl bg-[#030445] text-3xl font-bold text-white shadow-lg">A</span>
          <span>
            <span class="block text-4xl font-semibold tracking-[0.12em] text-[#030445]">ARQAU</span>
            <span class="mt-1 block text-sm tracking-wide text-[#8799b0]">{{ t('Панель управления') }}</span>
          </span>
        </div>

        <div class="mb-5 space-y-4">
          <h1 class="text-2xl font-normal text-[#4d5c6d]">{{ t('Вход в систему') }}</h1>
          <div class="h-px w-full bg-[#dae4f0]"></div>
        </div>

        <form class="flex flex-col gap-5" @submit.prevent="handleLogin">
          <div>
            <input
              v-model="email"
              type="text"
              autocomplete="username"
              required
              class="w-full rounded-[32px] border border-[#dae4f0] bg-[#fcfeff] px-4 py-3 text-base text-[#0b1536] outline-none transition placeholder:text-[#afbcca] focus:border-[#0b1536] focus:ring-1 focus:ring-[#0b1536]"
              :placeholder="t('Введите email или имя пользователя')"
            />
          </div>

          <div class="relative">
            <input
              v-model="password"
              :type="showPassword ? 'text' : 'password'"
              autocomplete="current-password"
              required
              class="w-full rounded-[32px] border border-[#dae4f0] bg-[#fcfeff] px-4 py-3 pr-12 text-base text-[#0b1536] outline-none transition placeholder:text-[#afbcca] focus:border-[#0b1536] focus:ring-1 focus:ring-[#0b1536]"
              :placeholder="t('Введите пароль')"
            />
            <button type="button" class="absolute right-4 top-1/2 -translate-y-1/2 text-[#8c98a7] hover:text-[#0b1536]" :aria-label="t('Показать пароль')" @click="showPassword = !showPassword">
              <svg class="h-[18px] w-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path v-if="showPassword" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M3 3l18 18M10.585 10.587a2 2 0 002.828 2.828M9.88 9.88l4.24 4.24M6.59 6.59A9.955 9.955 0 002.458 12C3.732 16.057 7.523 19 12 19c1.28 0 2.5-.24 3.62-.68M10.73 5.08A9.8 9.8 0 0112 5c4.478 0 8.268 2.943 9.542 7a9.98 9.98 0 01-1.5 2.92" />
              </svg>
            </button>
          </div>

          <label class="flex cursor-pointer items-center gap-2.5 text-sm text-black/80">
            <span class="flex h-5 w-5 items-center justify-center rounded" :class="rememberMe ? 'bg-[#030445]' : 'bg-[#dae4f0]'">
              <svg v-if="rememberMe" class="h-3.5 w-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7" /></svg>
            </span>
            <input v-model="rememberMe" type="checkbox" class="sr-only" />
            {{ t('Запомнить меня') }}
          </label>

          <p v-if="error" class="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">{{ error }}</p>

          <button type="submit" :disabled="loading" class="mt-1 flex w-full items-center justify-center rounded-xl bg-[#030445] px-6 py-3 text-base font-normal text-[#f8fafd] transition hover:bg-[#151a54] disabled:cursor-not-allowed disabled:opacity-60">
            <svg v-if="loading" class="mr-2 h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.4 0 0 5.4 0 12h4z"/></svg>
            {{ loading ? t('Вход...') : t('Войти') }}
          </button>
        </form>
      </div>

      <div class="mx-auto mt-auto flex flex-col items-center pt-10 text-center text-xs leading-5 text-[#8799b0]">
        <router-link
          to="/privacy-policy"
          class="text-[#030445]/80 underline decoration-[#dae4f0] underline-offset-4 transition hover:text-[#030445] hover:decoration-[#030445]"
        >
          {{ t('Политика конфиденциальности') }}
        </router-link>
      </div>
    </section>

    <section class="hidden p-4 pl-0 lg:block lg:w-1/2 xl:w-[55%]">
      <div class="relative h-full min-h-[680px] overflow-hidden rounded-[2.5rem] bg-[#101d35] shadow-lg">
        <div class="absolute -right-24 -top-20 h-[420px] w-[420px] rounded-full bg-[#0066ff]/80 blur-sm"></div>
        <div class="absolute -bottom-24 -left-24 h-[420px] w-[420px] rounded-full bg-[#bcfe00]/90"></div>
        <div class="absolute left-[16%] top-[20%] h-[58%] w-[68%] rotate-6 rounded-[3.5rem] border border-white/20 bg-white/10 shadow-2xl backdrop-blur-xl"></div>
        <div class="absolute inset-0 flex items-end p-10 text-white xl:p-16">
          <div class="relative z-10 w-full max-w-xl rounded-[2rem] border border-white/15 bg-[#071226]/85 p-8 shadow-2xl backdrop-blur-md">
            <span class="mb-6 block h-1.5 w-20 rounded-full bg-[#bcfe00]"></span>
            <h2 class="text-4xl font-semibold leading-tight">{{ t('Пространство для самопонимания и эмоционального благополучия') }}</h2>
            <p class="mt-5 text-lg leading-7 text-white/85">{{ t('Управляйте опросами, пользователями и материалами в единой системе') }}</p>
          </div>
        </div>
      </div>
    </section>
  </main>
</template>

<script>
import api from '@/services/api'
import { t, setLang, currentLang } from '@/i18n'

export default {
  name: 'LoginPage',
  data() {
    return {
      email: '',
      password: '',
      rememberMe: false,
      showPassword: false,
      loading: false,
      error: '',
      languages: [{ value: 'ru', label: 'RU' }, { value: 'kz', label: 'ҚАЗ' }]
    }
  },
  computed: {
    lang() { return currentLang.value }
  },
  methods: {
    t,
    switchLang(lang) { setLang(lang) },
    async handleLogin() {
      this.loading = true
      this.error = ''
      try {
        const response = await api.login(this.email, this.password, this.rememberMe)
        const next = this.$route.query.next
        if (next) this.$router.push(next)
        else if (response.data.user?.is_staff) this.$router.push('/staff/surveys')
        else if (response.data.user_type === 'doctor' || response.data.user?.type === 'doctor') this.$router.push('/doctor/passes')
        else this.$router.push('/surveys')
      } catch (error) {
        this.error = error.response?.data?.error || t('Не удалось войти')
      } finally {
        this.loading = false
      }
    }
  }
}
</script>
